import { useEffect, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, Input, Modal, SearchableSelect, Select } from "../ui";
import { abroadAdminApi } from "../../api/abroadLoans";
import { errorMessageOf, fieldErrorsOf, problemOf } from "../../api/scholarships";
import { COUNTRY_OPTIONS, useEducationLevels } from "../scholarships/model";

const EMPTY = { name: "", levelId: "", durationMonths: "", institutionName: "", countryCode: "" };

const formOf = (p) =>
  p ? { name: p.name, levelId: p.levelId, durationMonths: String(p.durationMonths), institutionName: p.institutionName ?? "", countryCode: p.countryCode ?? "" } : EMPTY;

const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.levelId) errors.levelId = "Level is required";
  const months = Number(form.durationMonths);
  if (!(Number.isInteger(months) && months >= 1 && months <= 120)) errors.durationMonths = "Enter whole months from 1 to 120";
  return errors;
};

// Adds a programme to the catalogue, or changes `programme`; the institution and country are optional, as a loan may cover a study level only
export default function ProgrammeDialog({ open, programme, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { data: levels = [] } = useEducationLevels({ keep: programme ? [{ id: programme.levelId, name: programme.levelName }] : [] });

  useEffect(() => {
    if (open) {
      setForm(formOf(programme));
      setErrors({});
    }
  }, [open, programme]);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    const body = {
      name: form.name.trim(),
      levelId: form.levelId,
      durationMonths: Number(form.durationMonths),
      institutionName: form.institutionName.trim() || null,
      countryCode: form.countryCode || null,
    };
    setSaving(true);
    try {
      const { data } = programme ? await abroadAdminApi.changeProgramme(programme.id, body) : await abroadAdminApi.createProgramme(body);
      toast.success(programme ? "Programme saved" : "Programme added");
      onSaved(data);
    } catch (error) {
      console.error(`Failed to save programme ${programme?.id ?? "(new)"}:`, error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to save the programme. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const field = (name) => ({ name, value: form[name], onChange: update, error: errors[name], disabled: saving });

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!saving}
      size="lg"
      title={programme ? "Edit programme" : "New programme"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="abroad-programme" loading={saving} icon={<CheckIcon className="h-4 w-4" />}>
            {programme ? "Save" : "Add programme"}
          </Button>
        </>
      }
    >
      <form id="abroad-programme" onSubmit={submit} noValidate className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Input label="Name" required maxLength={200} placeholder="e.g. Masters" {...field("name")} className="md:col-span-2" />
        <Select label="Level" required placeholder="Select a level" options={levels.map((l) => ({ value: l.id, label: l.levelName }))} {...field("levelId")} />
        <Input type="number" label="Duration" required min={1} max={120} step="1" suffix="months" {...field("durationMonths")} />
        <Input label="Institution" maxLength={200} placeholder="Leave empty: the student names it" {...field("institutionName")} />
        <SearchableSelect label="Country" placeholder="Leave empty: the student names it" options={COUNTRY_OPTIONS} {...field("countryCode")} />
      </form>
    </Modal>
  );
}
